/**
 * TEMPORARY mock of the Recargas automáticas backend (BD-10000), see ./index.ts.
 * Enabled with `REACT_APP_MOCK_API=automaticCharge`.
 *
 * Behaves like the backend: per-operator status (DISABLED → CONTRACT_PENDING on contract
 * acceptance → ENABLED on payout account), PAID-only daily report and a paginated day detail
 * whose rows add up to the day's summary. The history is generated from the date, so it is the
 * same on every load.
 *
 * Test scenarios from the browser console (then reload the page):
 *   automaticChargeMock.setStatus(0 | 1 | 2)   // DISABLED / CONTRACT_PENDING / ENABLED
 *   automaticChargeMock.setContractVersion('v0') // "Revisar termo" banner
 *   automaticChargeMock.setEmptyHistory(true)    // "Nenhuma recarga automática no período."
 *   automaticChargeMock.setPaymentConfigMissing(true) // PIX config without Pix Automático
 *                                                // support: "Habilitar" buttons disabled
 *   automaticChargeMock.failNext('status')       // next status call fails (also 'accept',
 *                                                // 'enable', 'update', 'daily', 'detail')
 *   automaticChargeMock.reset()                  // every operator back to DISABLED
 * Agência "0000" makes `enable` and `updatePayoutAccount` answer the backend's 400 for an
 * invalid agency. `updatePayoutAccount` answers 409 unless the operator is ENABLED, like the
 * backend.
 */

import { isMockEnabled, mockResponse, mockError, createMockStore } from '.';

const SETTINGS_STATUS = { DISABLED: 0, CONTRACT_PENDING: 1, ENABLED: 2 };
const FEE_PERCENTAGE = 2.5;
const HISTORY_DAYS = 120;
const ENABLE_CODE = 'automaticchargesettingsservice.saveaccountandenable';
const UPDATE_CODE = 'automaticchargesettingsservice.updatepayoutaccount';
const MOCK_EDITOR_NAME = 'Usuário de teste';

interface OperatorState {
  status: number;
  contractAcceptedAt: string | null;
  contractVersion: string | null;
  payoutAccount: any;
}

interface MockState {
  operators: Record<string, OperatorState>;
  // Applied to operators with no state of their own (set by the console helpers).
  defaultOperator: OperatorState | null;
  emptyHistory: boolean;
  failNext: string | null;
  // Simulates an operator PIX config that cannot carry Pix Automático.
  paymentConfigMissing: boolean;
}

const store = createMockStore<MockState>('automaticCharge', {
  operators: {},
  defaultOperator: null,
  emptyHistory: false,
  failNext: null,
  paymentConfigMissing: false,
});

const INITIAL_OPERATOR: OperatorState = {
  status: SETTINGS_STATUS.DISABLED,
  contractAcceptedAt: null,
  contractVersion: null,
  payoutAccount: null,
};

const getOperator = (transportOperatorId: string): OperatorState => {
  const state = store.get();
  return (
    state.operators[transportOperatorId] ||
    state.defaultOperator ||
    INITIAL_OPERATOR
  );
};

const SAMPLE_PAYOUT_ACCOUNT = {
  id: 'mock-payout-account',
  automaticChargeSettingId: 'mock-setting',
  bankCode: 'BB',
  bankName: 'Banco do Brasil',
  accountType: 0,
  agency: '1234',
  account: '56789-0',
  pixKey: 'financeiro@operadora.com.br',
  updatedAt: '2026-09-01T13:30:00.000Z',
  updatedByName: MOCK_EDITOR_NAME,
};

const saveOperator = (transportOperatorId: string, operator: OperatorState) => {
  const state = store.get();
  store.set({
    ...state,
    operators: { ...state.operators, [transportOperatorId]: operator },
  });
};

const updateAllOperators = (change: Partial<OperatorState>) => {
  const state = store.get();
  const operators = Object.keys(state.operators).reduce(
    (acc, id) => ({ ...acc, [id]: { ...state.operators[id], ...change } }),
    {},
  );
  const defaultOperator = {
    ...(state.defaultOperator || INITIAL_OPERATOR),
    ...change,
  };
  store.set({ ...state, operators, defaultOperator });
};

/**
 * Consumes a failure armed with `automaticChargeMock.failNext(call)`.
 * @param {string} call the call about to answer
 * @return {boolean} whether this call must fail
 */
const shouldFail = (call: string) => {
  const state = store.get();
  if (state.failNext !== call) return false;
  store.set({ ...state, failNext: null });
  return true;
};

const failGeneric = () =>
  mockError(500, 'error.generic', 'Erro simulado pelo mock da API');

// ---- Deterministic history -------------------------------------------------

const PASSENGERS = [
  'Ana Souza',
  'Bruno Lima',
  'Carla Mendes',
  'Diego Rocha',
  'Elaine Costa',
  'Fábio Nunes',
  'Gabriela Alves',
  'Henrique Dias',
  'Isabela Ferreira',
  'João Martins',
  '',
];
const AMOUNTS = [10, 20, 25, 30, 50, 100];

const seededRandom = (seed: number) => {
  let value = seed % 2147483647;
  if (value <= 0) value += 2147483646;
  return () => {
    value = (value * 16807) % 2147483647;
    return (value - 1) / 2147483646;
  };
};

const pad = (value: number, size = 2) => String(value).padStart(size, '0');

const toDay = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * The PAID recharges of one day, ordered by time. Some days have none, and every 7th day has
 * more than one page (for the pagination).
 * @param {string} day YYYY-MM-DD
 * @return {Array} the day's rows, in the backend detail shape
 */
const generateDay = (day: string) => {
  // Scrambled so consecutive days don't get near-identical random sequences.
  const seed = (Number(day.replace(/-/g, '')) * 48271) % 2147483647;
  const random = seededRandom(seed);
  const dayNumber = Number(day.slice(8, 10));
  let count = 23;
  if (dayNumber % 7 !== 0) {
    count = random() < 0.25 ? 0 : 1 + Math.floor(random() * 8);
  }

  return Array.from({ length: count }, (_, index) => {
    const valorDaRecarga = AMOUNTS[Math.floor(random() * AMOUNTS.length)];
    const seconds = Math.floor(((index + random()) / count) * 86399);
    const hora = `${pad(Math.floor(seconds / 3600))}:${pad(
      Math.floor((seconds % 3600) / 60),
    )}:${pad(seconds % 60)}`;
    const hex = Math.floor(random() * 0xffffffff)
      .toString(16)
      .padStart(8, '0');
    return {
      hora,
      id: `${hex}-${day.replace(/-/g, '').slice(0, 4)}-4${pad(
        index,
        3,
      )}-8000-${day.replace(/-/g, '')}${pad(index, 4)}`,
      passageiro: PASSENGERS[Math.floor(random() * PASSENGERS.length)],
      valorDaRecarga,
      valorDeRepasse: round2(valorDaRecarga * (1 - FEE_PERCENTAGE / 100)),
    };
  });
};

const historyDays = () => {
  const days: string[] = [];
  const today = new Date();
  for (let offset = 0; offset < HISTORY_DAYS; offset += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - offset);
    days.push(toDay(date));
  }
  return days;
};

// ---- Mocked service --------------------------------------------------------

const automaticChargeMockApi = {
  getStatus: ({ params }: { params: any }) => {
    if (shouldFail('status')) return failGeneric();
    return mockResponse({
      ...getOperator(params.transportOperatorId),
      paymentConfigReady: !store.get().paymentConfigMissing,
    });
  },

  acceptContract: ({ data }: { data: any }) => {
    if (shouldFail('accept')) return failGeneric();
    const operator = getOperator(data.transportOperatorId);
    const accepted = {
      ...operator,
      status:
        operator.status === SETTINGS_STATUS.ENABLED
          ? SETTINGS_STATUS.ENABLED
          : SETTINGS_STATUS.CONTRACT_PENDING,
      contractAcceptedAt: new Date().toISOString(),
      contractVersion: data.contractVersion,
    };
    saveOperator(data.transportOperatorId, accepted);
    return mockResponse(accepted);
  },

  enable: ({ data }: { data: any }) => {
    if (shouldFail('enable')) return failGeneric();
    const { transportOperatorId, ...account } = data;
    const operator = getOperator(transportOperatorId);
    if (operator.status === SETTINGS_STATUS.DISABLED) {
      return mockError(
        400,
        `${ENABLE_CODE}.setting.notfound`,
        'Contract must be accepted before enabling',
      );
    }
    if (account.agency === '0000') {
      return mockError(400, `${ENABLE_CODE}.agency.invalid`, 'Invalid agency');
    }
    const payoutAccount = {
      id: 'mock-payout-account',
      automaticChargeSettingId: `mock-setting-${transportOperatorId}`,
      ...account,
      updatedAt: new Date().toISOString(),
      updatedByName: MOCK_EDITOR_NAME,
    };
    const enabled = {
      ...operator,
      status: SETTINGS_STATUS.ENABLED,
      payoutAccount,
    };
    saveOperator(transportOperatorId, enabled);
    return mockResponse({ setting: enabled, payoutAccount });
  },

  updatePayoutAccount: ({ data }: { data: any }) => {
    if (shouldFail('update')) return failGeneric();
    const { transportOperatorId, ...account } = data;
    const operator = getOperator(transportOperatorId);
    if (operator.status !== SETTINGS_STATUS.ENABLED) {
      return mockError(
        409,
        `${UPDATE_CODE}.notenabled`,
        'Automatic charge is not ENABLED',
      );
    }
    if (account.agency === '0000') {
      return mockError(400, `${UPDATE_CODE}.agency.invalid`, 'Invalid agency');
    }
    const payoutAccount = {
      ...(operator.payoutAccount || SAMPLE_PAYOUT_ACCOUNT),
      ...account,
      updatedAt: new Date().toISOString(),
      updatedByName: MOCK_EDITOR_NAME,
    };
    saveOperator(transportOperatorId, { ...operator, payoutAccount });
    return mockResponse(payoutAccount);
  },

  dailyReport: ({ params }: { params: any }) => {
    if (shouldFail('daily')) return failGeneric();
    if (store.get().emptyHistory) return mockResponse([]);
    const start = toDay(new Date(Number(params.startDate)));
    const end = toDay(new Date(Number(params.endDate)));
    const rows = historyDays()
      .filter((day) => day >= start && day <= end)
      .map((usedDate) => {
        const recharges = generateDay(usedDate);
        return {
          usedDate,
          quantidadeRecargas: recharges.length,
          arrecadado: round2(
            recharges.reduce((sum, row) => sum + row.valorDaRecarga, 0),
          ),
          repassado: round2(
            recharges.reduce((sum, row) => sum + row.valorDeRepasse, 0),
          ),
        };
      })
      .filter((row) => row.quantidadeRecargas > 0)
      .sort((a, b) => (a.usedDate < b.usedDate ? -1 : 1));
    return mockResponse(rows);
  },

  historyDetail: ({ params }: { params: any }) => {
    if (shouldFail('detail')) return failGeneric();
    // Newest first, like the backend.
    const rows = store.get().emptyHistory
      ? []
      : generateDay(params.date).reverse();
    const { _page: pageParam, _limit: limitParam } = params;
    const page = Number(pageParam) || 1;
    const limit = Number(limitParam) || 20;
    return mockResponse(rows.slice((page - 1) * limit, page * limit), {
      totalcount: String(rows.length),
    });
  },
};

// Console helpers for the scenarios listed above.
if (isMockEnabled('automaticCharge') && typeof window !== 'undefined') {
  (window as any).automaticChargeMock = {
    setStatus: (status: number) =>
      updateAllOperators(
        status === SETTINGS_STATUS.DISABLED
          ? INITIAL_OPERATOR
          : {
              status,
              contractAcceptedAt: new Date().toISOString(),
              contractVersion: 'v1-2026-09',
              payoutAccount:
                status === SETTINGS_STATUS.ENABLED
                  ? SAMPLE_PAYOUT_ACCOUNT
                  : null,
            },
      ),
    setContractVersion: (contractVersion: string) =>
      updateAllOperators({ contractVersion }),
    setEmptyHistory: (emptyHistory: boolean) =>
      store.set({ ...store.get(), emptyHistory }),
    setPaymentConfigMissing: (paymentConfigMissing: boolean) =>
      store.set({ ...store.get(), paymentConfigMissing }),
    failNext: (call: string) => store.set({ ...store.get(), failNext: call }),
    reset: () => store.reset(),
    state: () => store.get(),
  };
}

export { automaticChargeMockApi };
export default automaticChargeMockApi;
